import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { OWNER_USER } from "@/config/proprietaireNav";
import { useSession } from "@/session/SessionProvider";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { fetchProfilProprietaire, saveProfilProprietaire } from "@/store/platform/profilEspace";
import { removeLogo, resolveLogoDataUrl, uploadLogoDataUrl } from "@/store/shared/logos";
import type { Preferences, Profil } from "./types";

interface ParametresContextValue {
  profil: Profil;
  preferences: Preferences;
  /** Chargement initial du profil depuis la base. */
  chargement: boolean;
  updateProfil: (profil: Profil) => Promise<void>;
  updatePreferences: (preferences: Preferences) => void;
}

const ParametresContext = createContext<ParametresContextValue | null>(null);

function decouperNom(full: string): { prenom: string; nom: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { prenom: "", nom: "" };
  if (parts.length === 1) return { prenom: parts[0], nom: "" };
  return { prenom: parts[0], nom: parts.slice(1).join(" ") };
}

const initialProfil: Profil = (() => {
  const { prenom, nom } = decouperNom(OWNER_USER.name);
  // Email et téléphone restent neutres tant qu'aucune donnée réelle n'existe.
  return {
    prenom,
    nom,
    email: "",
    telephone: "",
    whatsapp: "",
    logo: "",
    logoPath: "",
    adresse: "",
    siteWeb: "",
    ifu: "",
    rccm: "",
  };
})();

const initialPreferences: Preferences = {
  notifLoyer: true,
  notifPaiement: true,
  notifImpaye: true,
  notifContrat: true,
  notifEmail: false,
  langue: "fr",
  devise: "XOF",
  formatDate: "jj/mm/aaaa",
};

/**
 * Profil et préférences de l'utilisateur courant.
 * Le profil est lu et enregistré dans Supabase (`comptes` + `proprietaires`) :
 * seules les informations AUTORISÉES (nom, téléphone, adresse) sont écrites, la
 * protection serveur (RLS + trigger `proteger_compte`) empêchant toute
 * modification sensible. Les préférences restent locales à la session.
 */
export function ParametresProvider({ children }: { children: ReactNode }) {
  const { session, refresh } = useSession();
  const compteId = session?.id ?? "";
  const [profil, setProfil] = useState<Profil>(initialProfil);
  const [preferences, setPreferences] = useState<Preferences>(initialPreferences);
  const [chargement, setChargement] = useState(false);
  // Périmètre Storage du compte (propriétaire) : sert de dossier racine du logo.
  const [scopeId, setScopeId] = useState("");

  useEffect(() => {
    if (!compteId) return;
    let active = true;
    setChargement(true);
    (async () => {
      try {
        const data = await fetchProfilProprietaire(compteId);
        if (!active) return;
        const { prenom, nom } = decouperNom(data.nomComplet);
        setScopeId(data.proprietaireId);
        setProfil({
          prenom,
          nom,
          email: data.email,
          telephone: data.telephone,
          whatsapp: data.whatsapp,
          adresse: data.adresse,
          siteWeb: data.siteWeb,
          ifu: data.ifu,
          rccm: data.rccm,
          logo: "",
          logoPath: data.logoPath,
        });
        if (data.logoPath) {
          const resolved = await resolveLogoDataUrl(data.logoPath);
          if (active && resolved) setProfil((prev) => ({ ...prev, logo: resolved }));
        }
      } catch (caught) {
        dispatchDataError({
          scope: "Profil",
          message: "Impossible de charger votre profil.",
          technical: describeSupabaseError(caught),
        });
      } finally {
        if (active) setChargement(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [compteId]);

  const updateProfil = useCallback(
    async (next: Profil) => {
      if (!compteId) {
        setProfil(next);
        return;
      }
      // Logo : téléversement (nouveau), conservation ou suppression.
      let logoPath: string | null = next.logoPath || null;
      try {
        if (!next.logo) {
          logoPath = null;
          if (profil.logoPath) await removeLogo(profil.logoPath);
        } else if (scopeId && next.logo.startsWith("data:") && next.logo !== profil.logo) {
          logoPath = await uploadLogoDataUrl(scopeId, next.logo, profil.logoPath || undefined);
        }
      } catch (caught) {
        dispatchDataError({
          scope: "Profil",
          message: "Le téléversement du logo a échoué. Réessayez.",
          technical: describeSupabaseError(caught),
        });
        throw caught;
      }

      const nomComplet = `${next.prenom} ${next.nom}`.trim();
      await saveProfilProprietaire(compteId, {
        nomComplet,
        telephone: next.telephone,
        whatsapp: next.whatsapp,
        adresse: next.adresse,
        siteWeb: next.siteWeb,
        ifu: next.ifu,
        rccm: next.rccm,
        logoPath,
      });
      setProfil({ ...next, email: profil.email, logoPath: logoPath ?? "" });
      // La barre supérieure et le PDF reflètent immédiatement le nouveau nom.
      await refresh();
    },
    [compteId, scopeId, profil, refresh]
  );

  const updatePreferences = useCallback((next: Preferences) => setPreferences(next), []);

  const value = useMemo(
    () => ({ profil, preferences, chargement, updateProfil, updatePreferences }),
    [profil, preferences, chargement, updateProfil, updatePreferences]
  );

  return <ParametresContext.Provider value={value}>{children}</ParametresContext.Provider>;
}

export function useParametres(): ParametresContextValue {
  const context = useContext(ParametresContext);
  if (!context) {
    throw new Error("useParametres doit être utilisé à l'intérieur de ParametresProvider");
  }
  return context;
}